import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { UserInfo } from '../types';

interface UserContextType {
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;
  fetchUserInfo: (accessToken: string) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);

  const fetchUserInfo = useCallback(async (accessToken: string) => {
    try {
      // Fetch user info from Google OAuth2 API
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user info');
      }

      const data = await response.json();
      
      const userInfo: UserInfo = {
        email: data.email,
        name: data.name || data.email,
        picture: data.picture,
      };

      setUser(userInfo);
      
      // Store user info in sessionStorage for persistence
      sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    } catch (error) {
      // Try to load from sessionStorage as fallback
      const storedUser = sessionStorage.getItem('userInfo');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  }, []);

  // Load user info from sessionStorage on mount
  React.useEffect(() => {
    const storedUser = sessionStorage.getItem('userInfo');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, fetchUserInfo }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;